import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MainNavigation from '../../components/ui/MainNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import WelcomePanel from './components/WelcomePanel';
import MetricsCard from './components/MetricsCard';
import RFQStatusCard from './components/RFQStatusCard';
import RecommendedSolution from './components/RecommendedSolution';
import RecentOrderCard from './components/RecentOrderCard';
import NotificationPanel from './components/NotificationPanel';
import FeaturedServicesPanel from './components/FeaturedServicesPanel';
import UpcomingWebinarsPanel from './components/UpcomingWebinarsPanel';

const CustomerDashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mock user data
  useEffect(() => {
    const mockUser = {
      id: 1,
      name: "Sarah Nakamya",
      email: "sarah.nakamya@techcorp.ug",
      role: "customer",
      company: "TechCorp Uganda Ltd",
      joinDate: "2024-01-15",
      totalOrders: 12,
      totalSpent: 45000000
    };
    
    setUser(mockUser);
    setLoading(false);
  }, []);

  // Mock metrics data
  const metricsData = [
    {
      title: "Active RFQs",
      value: "8",
      subtitle: "3 pending responses",
      icon: "FileText",
      color: "primary",
      trend: { type: "up", value: "+2 this week" }
    },
    {
      title: "Pending Quotes",
      value: "5",
      subtitle: "Awaiting your review",
      icon: "Clock",
      color: "warning",
      trend: { type: "neutral", value: "No change" }
    },
    {
      title: "Completed Orders",
      value: "12",
      subtitle: "This year",
      icon: "CheckCircle",
      color: "success",
      trend: { type: "up", value: "+3 this month" }
    },
    {
      title: "Total Savings",
      value: "UGX 2.5M",
      subtitle: "Through bulk orders",
      icon: "TrendingUp",
      color: "accent",
      trend: { type: "up", value: "+15% vs last year" }
    }
  ];

  // Mock RFQ data
  const recentRFQs = [
    {
      id: "RFQ-2024-001",
      title: "Network Infrastructure Upgrade",
      status: "Quoted",
      submittedDate: "2024-09-15",
      responseDate: "2024-09-18",
      estimatedValue: 15000000
    },
    {
      id: "RFQ-2024-002",
      title: "Cloud Migration Services",
      status: "Under Review",
      submittedDate: "2024-09-12",
      responseDate: null,
      estimatedValue: 8500000
    },
    {
      id: "RFQ-2024-003",
      title: "Cybersecurity Assessment",
      status: "Approved",
      submittedDate: "2024-09-10",
      responseDate: "2024-09-14",
      estimatedValue: 5200000
    }
  ];

  // Mock recommended solutions
  const recommendedSolutions = [
    {
      id: 1,
      title: "Enterprise Network Security Suite",
      description: "Comprehensive network security solution with firewall, intrusion detection, and monitoring capabilities for enterprise environments.",
      category: "Cybersecurity",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=300&fit=crop",
      rating: 5,
      reviews: 24,
      price: 12000000,
      isNew: true,
      discount: null
    },
    {
      id: 2,
      title: "Cloud Infrastructure Management",
      description: "Complete cloud infrastructure setup and management services including migration, optimization, and 24/7 monitoring support.",
      category: "Infrastructure",
      image: "https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg?w=400&h=300&fit=crop",
      rating: 4,
      reviews: 18,
      price: 8500000,
      isNew: false,
      discount: 15
    },
    {
      id: 3,
      title: "Digital Transformation Consultancy",
      description: "Strategic consultancy services to guide your organization through digital transformation with proven methodologies and best practices.",
      category: "Consultancy",
      image: "https://images.pixabay.com/photo/2016/11/19/14/00/code-1839406_1280.jpg?w=400&h=300&fit=crop",
      rating: 5,
      reviews: 31,
      price: 6000000,
      isNew: false,
      discount: null
    }
  ];

  // Mock recent orders
  const recentOrders = [
    {
      id: "ORD-2024-045",
      status: "Delivered",
      orderDate: "2024-09-01",
      deliveryDate: "2024-09-08",
      total: 4500000,
      trackingNumber: "TT2024090145",
      canReorder: true,
      items: [
        { name: "Network Switches (24-port)", quantity: 4 },
        { name: "Ethernet Cables (Cat6)", quantity: 50 },
        { name: "Installation Service", quantity: 1 }
      ]
    },
    {
      id: "ORD-2024-044",
      status: "Processing",
      orderDate: "2024-08-28",
      deliveryDate: null,
      total: 7200000,
      trackingNumber: null,
      canReorder: false,
      items: [
        { name: "Server Hardware", quantity: 2 },
        { name: "Configuration Service", quantity: 1 }
      ]
    }
  ];

  // Mock notifications
  const notifications = [
    {
      id: 1,
      type: "rfq_update",
      title: "RFQ Quote Ready",
      message: "Your quote for Network Infrastructure Upgrade is ready for review. The estimated delivery time is 2-3 weeks.",
      timestamp: new Date(Date.now() - 30 * 60 * 1000),
      read: false,
      actionUrl: "/rfq-management?rfq=RFQ-2024-001",
      actionText: "View Quote"
    },
    {
      id: 2,
      type: "order_update",
      title: "Order Shipped",
      message: "Your order #ORD-2024-045 has been shipped and is on its way. Expected delivery: September 8th.",
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      read: false,
      actionUrl: "/order-history?order=ORD-2024-045",
      actionText: "Track Order"
    },
    {
      id: 3,
      type: "promotion",
      title: "Special Offer",
      message: "Get 20% off on all cybersecurity services this month. Limited time offer for valued customers.",
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
      read: true,
      actionUrl: "/product-catalog?category=cybersecurity",
      actionText: "Browse Offers"
    },
    {
      id: 4,
      type: "webinar",
      title: "Upcoming Webinar",
      message: "Join our webinar on \'Digital Transformation Strategies\' on September 25th at 2:00 PM EAT.",
      timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      read: true,
      actionUrl: "#",
      actionText: "Register"
    }
  ];

  // Mock featured services
  const featuredServices = [
    {
      id: 1,
      title: "IT Strategy & Planning",
      description: "Comprehensive IT strategy development and implementation planning for your organization\'s digital future.",
      category: "Strategy",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=100&h=100&fit=crop",
      isPopular: true
    },
    {
      id: 2,
      title: "Network Design & Implementation",
      description: "Custom network architecture design and professional implementation services for optimal performance.",
      category: "Networking",
      image: "https://images.pexels.com/photos/1181354/pexels-photo-1181354.jpeg?w=100&h=100&fit=crop",
      isPopular: false
    },
    {
      id: 3,
      title: "Software Development",
      description: "Custom software development solutions tailored to your business requirements and workflows.",
      category: "Software",
      image: "https://images.pixabay.com/photo/2016/11/30/20/58/programming-1873854_1280.png?w=100&h=100&fit=crop",
      isPopular: true
    }
  ];

  // Mock webinars
  const upcomingWebinars = [
    {
      id: 1,
      title: "Digital Transformation Strategies for SMEs",
      description: "Learn practical approaches to digital transformation that can help small and medium enterprises compete effectively in the digital age.",
      date: "2024-09-25T14:00:00",
      speaker: "Dr. James Mukasa",
      attendees: 156,
      isFree: true
    },
    {
      id: 2,
      title: "Cybersecurity Best Practices",
      description: "Essential cybersecurity practices every organization should implement to protect against modern threats and vulnerabilities.",
      date: "2024-09-28T10:00:00",
      speaker: "Sarah Namukasa",
      attendees: 89,
      isFree: true
    },
    {
      id: 3,
      title: "Cloud Migration Planning Workshop",
      description: "Step-by-step guide to planning and executing successful cloud migration projects with minimal business disruption.",
      date: "2024-10-02T15:00:00",
      speaker: "Michael Ssebunya",
      attendees: 67,
      isFree: false
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation user={user} rfqCount={0} />
        <div className="pt-16">
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
            <div className="text-center">
              <Icon name="Loader2" size={32} className="animate-spin mx-auto mb-4 text-primary" />
              <p className="text-muted-foreground">Loading your dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation user={user} rfqCount={recentRFQs?.length} />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbNavigation />
          
          {/* Welcome Panel */}
          <WelcomePanel user={user} />
          
          {/* Metrics Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {metricsData?.map((metric, index) => (
              <MetricsCard key={index} {...metric} />
            ))}
          </div>
          
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Recent RFQ Status Updates */}
              <section>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-foreground">Recent RFQ Updates</h2>
                  <Link to="/rfq-management">
                    <Button variant="outline" iconName="ArrowRight">
                      View All RFQs
                    </Button>
                  </Link>
                </div>
                
                <div className="grid gap-4">
                  {recentRFQs?.map((rfq) => (
                    <RFQStatusCard key={rfq?.id} rfq={rfq} />
                  ))}
                </div>
              </section>
              
              {/* Recommended Solutions */}
              <section>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-foreground">Recommended for You</h2>
                  <Link to="/product-catalog">
                    <Button variant="outline" iconName="ArrowRight">
                      Browse Catalog
                    </Button>
                  </Link>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {recommendedSolutions?.map((solution) => (
                    <RecommendedSolution key={solution?.id} solution={solution} />
                  ))}
                </div>
              </section>
              
              {/* Recent Orders */}
              <section>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-foreground">Recent Orders</h2>
                  <Link to="/order-history">
                    <Button variant="outline" iconName="ArrowRight">
                      View Order History
                    </Button>
                  </Link>
                </div>
                
                <div className="grid gap-4">
                  {recentOrders?.map((order) => (
                    <RecentOrderCard key={order?.id} order={order} />
                  ))}
                </div>
              </section>
            </div>
            
            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* Notifications */}
              <NotificationPanel notifications={notifications} />
              
              {/* Featured Services */}
              <FeaturedServicesPanel services={featuredServices} />
              
              {/* Upcoming Webinars */}
              <UpcomingWebinarsPanel webinars={upcomingWebinars} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;